import { Injectable } from '@angular/core';
import { IndividualConfig, ToastrService, ToastContainerDirective } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AtlasToastService {
  private globalOptions: Partial<IndividualConfig> = {
    timeOut: 6000,
    tapToDismiss: true,
    extendedTimeOut: 4000,
    closeButton: false
  };

  constructor(private toastr: ToastrService, private translateService: TranslateService) {}

  public openToast(
    titleLabel: string,
    messageLabel: string,
    extraMsg: string,
    timeout: number = 6000,
    type: string,
    overlayContainer: ToastContainerDirective,
    translationParams = {}
  ): void {
    const options: Partial<IndividualConfig> | any = Object.assign({}, this.globalOptions);

    if (timeout === -1) {
      options.disableTimeOut = true;
    } else {
      options.timeOut = timeout;
    }

    options.enableHtml = true;

    if (overlayContainer !== null) {
      this.toastr.overlayContainer = overlayContainer;
      options.positionClass = 'inline';
    } else {
      this.toastr.overlayContainer = undefined;
      options.positionClass = 'toast-top-right';
    }

    const strings = [];

    if (titleLabel) {
      strings.push(this.translateService.get(titleLabel, translationParams));
    }

    if (messageLabel) {
      strings.push(this.translateService.get(messageLabel, translationParams));
    }

    if (extraMsg) {
      strings.push(of(extraMsg));
    }

    forkJoin(strings).subscribe((labels: string[]) => {
      switch (type) {
        case 'success':
          this.toastr.success(labels[1], labels[0], options);
          break;
        case 'error':
          this.toastr.error(labels[1], labels[0], options);
          break;
        case 'warning':
          this.toastr.warning(labels[1], labels[0], options);
          break;
        case 'info':
          options.tapToDismiss = false;
          options.closeButton = true;
          this.toastr.info(labels[1], labels[0], options);
          break;
        case 'success-reset-email':
          options.tapToDismiss = false;
          const mail = this.maskingEmailAddrs(labels[2]);
          const msg: string = labels[1] + ' ' + mail;
          this.toastr.success(msg, labels[0], options);
          break;
        default:
          options.toastClass = 'ngx-toastr  toast-notify';
          this.toastr.info(labels[1], labels[0], options);
      }
    });
  }

  success(titleLabel: string, messageLabel: string, timeout: number = 6000, overlayContainer: ToastContainerDirective = null): void {
    this.openToast(titleLabel, messageLabel, null, timeout, 'success', overlayContainer);
  }

  info(
    titleLabel: string,
    messageLabel: string,
    timeout: number = 6000,
    overlayContainer: ToastContainerDirective = null,
    translationParams = {}
  ): void {
    this.openToast(titleLabel, messageLabel, null, timeout, 'info', overlayContainer, translationParams);
  }

  warning(titleLabel: string, messageLabel: string, timeout: number = 6000, overlayContainer: ToastContainerDirective = null): void {
    this.openToast(titleLabel, messageLabel, null, timeout, 'warning', overlayContainer);
  }

  error(titleLabel: string, messageLabel: string, timeout: number = 6000, overlayContainer: ToastContainerDirective = null): void {
    this.openToast(titleLabel, messageLabel, null, timeout, 'error', overlayContainer);
  }

  notify(titleLabel: string, messageLabel: string, timeout: number = 6000, overlayContainer: ToastContainerDirective = null): void {
    this.openToast(titleLabel, messageLabel, null, timeout, 'notify', overlayContainer);
  }

  successResetPwdExpired(
    titleLabel: string,
    messageLabel: string,
    email: string,
    timeout: number = 6000,
    overlayContainer: ToastContainerDirective = null
  ): void {
    this.openToast(titleLabel, messageLabel, email, timeout, 'success-reset-email', overlayContainer);
  }

  private maskingEmailAddrs(email: string) {
    if (email) {
      let i = 0;
      const split = email.split('@');
      let mask = split[0];
      let domain = split[1];
      const topLevel = split[1].split('.');
      mask = mask.replace(/(..)(.{2}|$)/g, (beforeAt, string1, string2) => string1 + this.checkSpecialChar(string2));
      domain = topLevel[0].replace(
        /(..)(.{2}|$)/g,
        (beforeAt, string1, string2) => this.getMaskOrString1((i = i + 1), string1) + '*'.repeat(string2.length)
      );
      return mask + '@' + domain + '.' + topLevel[1];
    }
    return '';
  }

  private checkSpecialChar(part2: string) {
    const res = '*'.repeat(part2.length);
    if (part2[0] && part2[0].match(/[+-_.0-9]/)) {
      if (part2[1] && part2[1].match(/[+-_.0-9]/)) {
        return part2 + res;
      } else {
        return part2[0] + res;
      }
    } else if (part2[1] && part2[1].match(/[+-_.0-9]/)) {
      return res + part2[1];
    }
    return res;
  }
  // start domain with mask when value = 1
  private getMaskOrString1(value, part1) {
    return value === 1 ? '*' : part1;
  }
}
